import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, TypeBadge, Empty } from "../components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { FileText, Search, Paperclip, Send, Zap, Trash2, Loader2, Filter } from "lucide-react";
const selCls = "px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none";

export default function Opportunities() {
  const { user, meta } = useAuth();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: "", district: "", sector: "", q: "" });
  const [responded, setResponded] = useState({});
  const [active, setActive] = useState(null); // opportunity for respond dialog
  const [message, setMessage] = useState("");
  const [respAuto, setRespAuto] = useState(false);
  const [respAttachment, setRespAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewResponses, setViewResponses] = useState(null);
  const [respList, setRespList] = useState([]);
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoList, setAutoList] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);

  const canManage = user.role === "buyer" || user.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filters.type) params.type = filters.type;
    if (filters.district) params.district = filters.district;
    if (filters.sector) params.sector = filters.sector;
    if (user.role === "buyer") params.mine_only = true;
    try {
      const { data } = await api.get("/opportunities", { params });
      setOpps(data);
    } finally {
      setLoading(false);
    }
  }, [filters, user.role]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user.role === "smme") {
      api.get("/responses/me").then(({ data }) => {
        const m = {};
        data.forEach((r) => { m[r.opportunity_id] = r.status; });
        setResponded(m);
      });
    }
  }, [user.role]);

  const filtered = opps.filter((o) =>
    !filters.q || o.title.toLowerCase().includes(filters.q.toLowerCase()) ||
    (o.description || "").toLowerCase().includes(filters.q.toLowerCase())
  );

  const openRespond = (opp, auto) => {
    setActive(opp);
    setRespAuto(auto);
    setRespAttachment(null);
    setMessage(auto ? "We are interested in this opportunity and will submit full documentation." : "");
  };

  const respond = async (opp) => {
    setSubmitting(true);
    try {
      let attach = {};
      if (respAttachment) {
        const fd = new FormData();
        fd.append("file", respAttachment);
        const { data } = await api.post("/responses/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attach = { attachment_id: data.file_id, attachment_path: data.storage_path, attachment_name: data.original_filename };
      }
      await api.post(`/opportunities/${opp.id}/respond`, { message, auto: respAuto, ...attach });
      toast.success(respAuto ? "Auto-response sent to buyer" : "Response sent to buyer");
      setResponded({ ...responded, [opp.id]: "Submitted" });
      setActive(null);
      setMessage("");
      setRespAttachment(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this opportunity?")) return;
    await api.delete(`/opportunities/${id}`);
    toast.success("Opportunity deleted");
    load();
  };

  const openResponses = async (opp) => {
    setViewResponses(opp);
    const { data } = await api.get(`/opportunities/${opp.id}/responses`);
    setRespList(data);
  };

  const openAutoReplies = async () => {
    setAutoOpen(true);
    setAutoLoading(true);
    try {
      const { data } = await api.get("/responses/auto");
      setAutoList(data);
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Procurement" title={user.role === "buyer" ? "My Opportunities" : "Opportunities"}
        subtitle="RFQ, Tender, RFI and RFP postings from Assmang mining operations."
        action={canManage && (
          <button data-testid="view-all-auto-replies-button" onClick={openAutoReplies}
            className="inline-flex items-center gap-2 bg-[#F2B50C] hover:bg-[#D99E00] text-[#0A2540] font-semibold px-5 py-2.5 rounded-md transition-colors">
            <Zap className="h-4 w-4" /> All Auto-Replies
          </button>
        )} />

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-slate-500 text-sm font-medium"><Filter className="h-4 w-4" /> Filters</div>
        {canManage && (
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="opportunity-search-input" placeholder="Search opportunities…" value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
          </div>
        )}
        <select data-testid="opportunity-type-filter" className={selCls} value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
          <option value="">All Types</option>
          {(meta?.opportunity_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {canManage && (
          <>
            <select data-testid="opportunity-district-filter" className={selCls} value={filters.district} onChange={(e) => setFilters({ ...filters, district: e.target.value })}>
              <option value="">All Districts</option>
              {(meta?.districts || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select data-testid="opportunity-sector-filter" className={selCls} value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })}>
              <option value="">All Sectors</option>
              {(meta?.sectors || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </>
        )}
      </div>

      {loading ? (
        <Loader2 className="h-6 w-6 animate-spin text-[#00427A]" />
      ) : filtered.length === 0 ? (
        <Empty icon={FileText} title="No opportunities found" subtitle="Try adjusting your filters." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {filtered.map((o) => (
            <div key={o.id} data-testid={`opportunity-card-${o.id}`} className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <TypeBadge type={o.type} />
                  {o.broadcast_all && <span className="text-[10px] font-semibold uppercase bg-[#F2B50C]/10 text-[#F2B50C] px-2 py-0.5 rounded">Broadcast</span>}
                </div>
                <span className="text-xs text-slate-400 whitespace-nowrap">Closes {o.closing_date || "—"}{o.closing_time ? ` · ${o.closing_time}` : ""}</span>
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 leading-snug">{o.title}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-3 flex-1">{o.description}</p>
              <div className="flex flex-wrap gap-2 mt-4 text-xs text-slate-500">
                <span className="bg-slate-100 px-2 py-1 rounded">{o.mine_name}</span>
                {o.district && <span className="bg-slate-100 px-2 py-1 rounded">{o.district}</span>}
                {o.sector && <span className="bg-slate-100 px-2 py-1 rounded">{o.sector}</span>}
              </div>
              <div className="flex items-center justify-between gap-2 mt-5 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  {o.attachment_path ? (
                    <a data-testid={`opportunity-attachment-${o.id}`} href={fileUrl(o.attachment_path)} target="_blank" rel="noreferrer"
                      className="text-xs font-semibold text-[#00427A] flex items-center gap-1 hover:underline">
                      <Paperclip className="h-3.5 w-3.5" /> {o.attachment_name || "Download spec"}
                    </a>
                  ) : o.attachment_id && (
                    <span className="text-xs text-slate-500 flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> Attachment</span>
                  )}
                  {canManage && <span className="text-xs text-slate-500">{o.response_count} responses</span>}
                </div>
                <div className="flex items-center gap-2">
                  {user.role === "smme" ? (
                    responded[o.id] ? (
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-md">Responded · {responded[o.id]}</span>
                    ) : (
                      <>
                        <button data-testid={`opportunity-auto-respond-${o.id}`} onClick={() => openRespond(o, true)} disabled={submitting}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F2B50C] border border-[#F2B50C]/30 hover:bg-[#F2B50C]/5 px-3 py-1.5 rounded-md">
                          <Zap className="h-3.5 w-3.5" /> Auto
                        </button>
                        <button data-testid={`opportunity-respond-button-${o.id}`} onClick={() => openRespond(o, false)}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#00427A] hover:bg-[#00325C] text-white px-3 py-1.5 rounded-md">
                          <Send className="h-3.5 w-3.5" /> Respond
                        </button>
                      </>
                    )
                  ) : (
                    <>
                      <button data-testid={`opportunity-view-responses-${o.id}`} onClick={() => openResponses(o)}
                        className="text-sm font-semibold text-[#00427A] border border-slate-300 hover:bg-slate-50 px-3 py-1.5 rounded-md">
                        View Responses
                      </button>
                      <button data-testid={`opportunity-delete-${o.id}`} onClick={() => remove(o.id)}
                        className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 className="h-4 w-4" /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Respond dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            {respAuto && <Zap className="h-5 w-5 text-[#F2B50C]" />}
            {respAuto ? "Auto-Respond to" : "Respond to"} {active?.title}
          </DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Your response and any attachment are emailed to the buyer who posted this opportunity.</p>
          <textarea data-testid="respond-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
            placeholder="Describe your capability, pricing or availability…"
            className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
          <div>
            <label data-testid="respond-attachment-label" className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer border border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-md">
              <Paperclip className="h-4 w-4" /> {respAttachment ? "Change document" : "Attach document (optional)"}
              <input data-testid="respond-attachment-input" type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={(e) => setRespAttachment(e.target.files[0])} />
            </label>
            {respAttachment && (
              <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> {respAttachment.name}
                <button type="button" onClick={() => setRespAttachment(null)} className="text-red-500 hover:underline ml-1">remove</button>
              </p>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setActive(null)} className="px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
            <button data-testid="respond-submit-button" onClick={() => respond(active)} disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#00427A] hover:bg-[#00325C] text-white font-semibold px-5 py-2 rounded-md disabled:opacity-60">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} {respAuto ? "Send Auto-Response" : "Submit Response"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Responses viewer */}
      <Dialog open={!!viewResponses} onOpenChange={(o) => !o && setViewResponses(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Responses · {viewResponses?.title}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3">
            {respList.length === 0 ? <p className="text-sm text-slate-500">No responses yet.</p> :
              respList.map((r) => (
                <div key={r.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-900">{r.company_name || r.responder_name}</p>
                    {r.auto && <span className="text-[10px] font-semibold bg-[#F2B50C]/10 text-[#F2B50C] px-2 py-0.5 rounded">AUTO</span>}
                  </div>
                  <p className="text-xs text-slate-500">{r.responder_name} · {r.email}</p>
                  {r.message && <p className="text-sm text-slate-700 mt-2">{r.message}</p>}
                  {r.attachment_path && (
                    <a data-testid={`response-attachment-${r.id}`} href={fileUrl(r.attachment_path)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-[#00427A] hover:underline">
                      <Paperclip className="h-3.5 w-3.5" /> {r.attachment_name || "Download attachment"}
                    </a>
                  )}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* All auto-replies viewer */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-[#F2B50C]" /> All Auto-Replies</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3" data-testid="auto-replies-list">
            {autoLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-[#00427A]" />
            ) : autoList.length === 0 ? (
              <p className="text-sm text-slate-500">No auto-replies received yet.</p>
            ) : (
              autoList.map((r) => (
                <div key={r.id} data-testid={`auto-reply-${r.id}`} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">{r.company_name || r.responder_name}</p>
                    <span className="text-[10px] font-semibold bg-[#F2B50C]/10 text-[#F2B50C] px-2 py-0.5 rounded">AUTO</span>
                  </div>
                  <p className="text-xs text-slate-500">{r.responder_name} · {r.email}</p>
                  <p className="text-xs text-[#00427A] font-medium mt-1.5">
                    {r.opportunity_type ? `${r.opportunity_type} · ` : ""}{r.opportunity_title || "Opportunity"}
                  </p>
                  {r.message && <p className="text-sm text-slate-700 mt-2">{r.message}</p>}
                  {r.attachment_path && (
                    <a data-testid={`auto-reply-attachment-${r.id}`} href={fileUrl(r.attachment_path)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-[#00427A] hover:underline">
                      <Paperclip className="h-3.5 w-3.5" /> {r.attachment_name || "Download attachment"}
                    </a>
                  )}
                  <p className="text-[11px] text-slate-400 mt-2">{(r.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
